// Combines global providers and layout elements.
// Flex shell: chrome + scrollable main (single vertical scroll, no body overflow).
import { useLocation } from 'react-router-dom';
import { ProtectedRoute } from '@/features/auth';
import {
  FilterProvider,
  TasksProvider,
  TaskMutationsProvider,
} from '@/features/tasks';
import { FocusProvider } from '@/features/today';
import { GoBarProvider, GoBarModal } from '@/features/search';
import { ToastProvider } from '@/features/ui/context/ToastContext';
import ToastContainer from '@/features/ui/components/ToastContainer';
import AppLayoutInner from '@/components/layout/AppLayoutInner';
import { ThemeProvider } from '@mui/material/styles';
import { Box } from '@mui/material';
import theme from '@/styles/theme';
import { GCalProvider } from '@/features/planning/context/GCalContext';
import TaskModal from '@/features/tasks/components/TaskModal';
import { ProjectLimitWallProvider } from '@/features/wall';
import AppBarHeader from '@/components/layout/AppBarHeader';
import MobileQuickBar from '@/components/layout/MobileQuickBar';
import SkipToContentLink from '@/components/layout/SkipToContentLink';
import {
  isBattlefieldBreakpoint,
  useBreakpoint,
} from '@/hooks/ui/useBreakpoint';
import { useAltPageNav } from '@/hooks/ui/useAltPageNav';
import { useQuickAddFocusShortcut } from '@/hooks/ui/useQuickAddFocusShortcut';
import OnboardingProfileGate from '@/features/onboarding/components/OnboardingProfileGate';
import { isOnboardingWelcomePath } from '@/constants/onboardingRoutes';
import EmailVerificationBanner from '@/features/auth/components/EmailVerificationBanner';

const MOBILE_QUICKBAR_PADDING = 'calc(80px + env(safe-area-inset-bottom, 0px))';

const AppLayout = ({ children }) => {
  const location = useLocation();
  const currentBreakpoint = useBreakpoint();
  const isBattlefield = isBattlefieldBreakpoint(currentBreakpoint);
  const isWelcomeRoute = isOnboardingWelcomePath(location.pathname);
  const showMobileQuickBar = isBattlefield && !isWelcomeRoute;
  const contentFirstTabOrder =
    !isBattlefield &&
    (location.pathname.startsWith('/app/today') ||
      location.pathname.startsWith('/app/plan') ||
      location.pathname.startsWith('/app/organize') ||
      location.pathname.startsWith('/app/dashboard'));
  useAltPageNav();
  useQuickAddFocusShortcut();

  const mainContent = (
    <AppLayoutInner>
      <Box
        id="main-content"
        tabIndex={-1}
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowX: 'hidden',
          overflowY: 'auto',
          bgcolor: 'background.default',
          pb: showMobileQuickBar ? MOBILE_QUICKBAR_PADDING : 0,
          scrollPaddingBottom: showMobileQuickBar
            ? MOBILE_QUICKBAR_PADDING
            : undefined,
          ...(contentFirstTabOrder ? { order: 2 } : {}),
        }}
      >
        <OnboardingProfileGate>{children}</OnboardingProfileGate>
      </Box>
    </AppLayoutInner>
  );

  const appChrome = (
    <AppBarHeader contentFirstTabOrder={contentFirstTabOrder} />
  );

  return (
    <ProtectedRoute>
      <ThemeProvider theme={theme}>
        <ToastProvider>
          <FilterProvider>
            <TasksProvider>
              <ProjectLimitWallProvider>
              <FocusProvider>
                <TaskMutationsProvider>
                  <GCalProvider>
                    <GoBarProvider>
                      <Box
                        id="app-shell"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100dvh',
                          maxHeight: '100dvh',
                          overflow: 'hidden',
                          bgcolor: 'background.default',
                        }}
                      >
                        <SkipToContentLink disabled={contentFirstTabOrder} />
                        <EmailVerificationBanner />
                        {contentFirstTabOrder ? (
                          <>
                            {mainContent}
                            {appChrome}
                          </>
                        ) : (
                          <>
                            {appChrome}
                            {mainContent}
                          </>
                        )}
                        {showMobileQuickBar ? <MobileQuickBar /> : null}
                      </Box>
                      <GoBarModal />
                      <TaskModal />
                      <ToastContainer />
                    </GoBarProvider>
                  </GCalProvider>
                </TaskMutationsProvider>
              </FocusProvider>
              </ProjectLimitWallProvider>
            </TasksProvider>
          </FilterProvider>
        </ToastProvider>
      </ThemeProvider>
    </ProtectedRoute>
  );
};

export default AppLayout;
